'use client';
import {
  useState, useRef, useEffect,
} from 'react';
import * as is from 'is';
import { getAppliances } from '@utils/graphql/creators/appliances';
import applianceClient from '@utils/graphql/creators/appliance';
import { DashboardApplianceCache } from './cache';
import { useEffectOnce } from '@utils/react/hooks';
import { useMessageEmitter } from '@utils/react/eventEmitterContext';
import { SSOT_ACTIONS } from '@utils/eventHandler';
import { HorizontalBar } from '@skyline-platform/alchemy';
import { formatStreamCounts } from '@utils/formatter';
import { getLogger } from '@utils/general';
import './page.css';

const log = getLogger('src.pages.dashboard.page');

const Dashboard = () => {
  const [appliances, setAppliances] = useState(undefined);
  const chartNavRef = useRef({ index: 0, total: 0 });

  useEffect(() => {
    if (is.array(appliances)) {
      chartNavRef.current.total = formatStreamCounts(appliances).length;
    }
  }, [appliances]);

  const cache = new DashboardApplianceCache({ autoAdd: true });
  const connectionCountDebounceRef = useRef(null);

  // Debounce setAppliances for connection_count events: during a stream failure storm
  // a single SFS box can emit hundreds of Appliance connection_count events per minute.
  // Batching these into a single render per 250 ms prevents cascading re-renders.
  const flushConnectionCountUpdate = () => {
    if (connectionCountDebounceRef.current) clearTimeout(connectionCountDebounceRef.current);
    connectionCountDebounceRef.current = setTimeout(() => {
      setAppliances(cache.getData());
      connectionCountDebounceRef.current = null;
    }, 250);
  };

  cache.addEventHandler((data) => setAppliances(data), '*');

  useEffectOnce(() => {
    const { query, execute } = getAppliances();
    query.addReturnProperties('id', 'name', 'total_connection_count', 'stream_count');
    execute()
      .then(({
        error,
        data,
        isLoading,
      }) => {
        if (isLoading) {
          return setAppliances(isLoading);
        }
        if (error) {
          return setAppliances(error);
        }
        // adding an appliance to the cache then setting appliances state to the cache data to ensure
        // the data structure is consistent with subsequent updates received via subscriptions
        for (const appliance of data) {
          cache.addAppliance(appliance.id, appliance.name, appliance.stream_count, appliance.total_connection_count);
        }
        setAppliances(cache.getData());
      })
      .catch(() => setAppliances([]));
  });

  async function getApplianceName(applianceId) {
    const { query, execute } = applianceClient._appliance(applianceId);
    query.addReturnProperties('id', 'name', 'total_connection_count');
    try {
      const { data } = await execute();
      return data.name;
    } catch (error) {
      log.error(error);
      throw error;
    }
  }

  // Set up subscriptions
  const messageHandler = useMessageEmitter();
  async function handleUpdate(...args) {
    const updates = [...args];
    for (const update of updates) {
      if (!update.detail?.payload) {
        continue;
      }
      const {
        action,
        model,
        data,
      } = update.detail.payload;

      const parsedData = JSON.parse(data);

      if (model === 'Stream') {
        if (action === SSOT_ACTIONS.CREATE || action === SSOT_ACTIONS.UPDATE) {
          // add appliance to the cache when the stream is newly associated to an appliance
          const applianceId = parsedData.current.appliance_id;
          if (!cache.isApplianceInCache(applianceId)) {
            try {
              const applianceName = await getApplianceName(applianceId);
              cache.addAppliance(applianceId, applianceName);
            } catch (error) {
              log.error('Error adding associated appliance to cache: ', error);
            }
          }
        }

        switch (action) {
          case SSOT_ACTIONS.CREATE:
            cache.add(parsedData.current);
            break;
          case SSOT_ACTIONS.UPDATE:
            // possible updates - appliance_id and connection_number
            cache.update(parsedData.current, parsedData.previous);
            break;
          case SSOT_ACTIONS.REMOVE:
            cache.remove(parsedData.previous);
            break;
          default:
            return;
        }
      } else if (model === 'Appliance') {
        switch (action) {
          case SSOT_ACTIONS.UPDATE:
            if (parsedData.current.name !== parsedData.previous.name) {
              cache.updateApplianceName(parsedData.current);
            }
            if (parsedData.current.total_connection_count !== parsedData.previous.total_connection_count) {
              cache.updateApplianceConnectionCount(parsedData.current, { silent: true });
              flushConnectionCountUpdate();
              return;
            }
            break;
          case SSOT_ACTIONS.REMOVE:
            cache.removeAppliance(parsedData.current);
            break;
          default:
            return;
        }
      }
    }
  }

  useEffectOnce(() => {
    messageHandler.subscribe(handleUpdate);

    return () => {
      messageHandler.unsubscribe(handleUpdate);
    };
  });

  if (appliances === undefined || appliances === true) return (<span>Loading...</span>);
  if (is.array(appliances)) {
    const formattedAppliances = formatStreamCounts(appliances);

    return (
      <HorizontalBar
        data={formattedAppliances}
        dataKeys={['stream_count', 'connection_count']}
        yDataKey='name'
        title='Connections By Appliance'
      />
    );
  }
};

export default Dashboard;
